import { useState, useEffect, useRef, useMemo, ReactNode, memo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useCompanyMemberships } from '@/hooks/useCompanyMemberships';
import { supabase } from '@/integrations/supabase/client';
import { SharedLocationContext } from './SharedLocationContext';
import { useWhyDidYouUpdate } from '@/lib/debug';

interface Location {
  id: string;
  name: string;
  code?: string;
  company_id?: string;
}

// Export the shared hook
export { useLocation } from './SharedLocationContext';

interface LimitedLocationProviderProps {
  children: ReactNode;
}

/**
 * LimitedLocationProvider - Minimal location provider for PIN sessions
 * 
 * This provider ONLY reads location data from localStorage (PIN session)
 * and NEVER makes any database calls or sets up subscriptions.
 * 
 * It's specifically designed for /limited/* routes to prevent the infinite
 * loop issues that occur when LocationContext tries to initialize for PIN sessions.
 */
const LimitedLocationProviderComponent = ({ children }: LimitedLocationProviderProps) => {
  const { user, profile, loginMethod } = useAuth();
  // Phase A.1 PR 3: prefer primary membership company_id, fall back to
  // profile.company_id. Identical value for every current user.
  const { resolvedCompanyId } = useCompanyMemberships();
  const effectiveCompanyId = resolvedCompanyId ?? profile?.company_id ?? null;
  const [isLoading, setIsLoading] = useState(true);
  const hasInitialized = useRef(false);
  
  // CRITICAL FIX: Use ref for location to keep object reference stable
  const locationRef = useRef<Location | null>(null);
  
  // PHASE 4: Circuit breaker to detect infinite loops
  const renderCount = useRef(0);
  const renderTimestamp = useRef(Date.now());
  
  useEffect(() => {
    const now = Date.now();
    const timeDiff = now - renderTimestamp.current;
    
    // Reset counter every second
    if (timeDiff > 1000) {
      renderCount.current = 0;
      renderTimestamp.current = now;
    }
    
    renderCount.current++;
    
    // If more than 50 renders in 1 second, we have a loop
    if (renderCount.current > 50) {
      console.error('[LIMITED_LOCATION_PROVIDER] 🚨 INFINITE LOOP DETECTED! Pausing updates...');
      console.error('[LIMITED_LOCATION_PROVIDER] Run localStorage.setItem("RESET_LOOP", "1") to reset');
      
      // Check if user wants to reset
      if (localStorage.getItem('RESET_LOOP') === '1') {
        console.log('[LIMITED_LOCATION_PROVIDER] ✅ Reset triggered, clearing counter');
        localStorage.removeItem('RESET_LOOP');
        renderCount.current = 0;
        renderTimestamp.current = Date.now();
      }
    }
  });
  
  // DEBUG: Track what causes re-renders
  useWhyDidYouUpdate('LimitedLocationProvider', { 
    user: user?.id, 
    profile: profile?.id, 
    loginMethod,
    locationId: locationRef.current?.id,
    isLoading 
  });
  
  // PHASE 1: Handle PIN sessions (runs once on mount, no auth dependencies)
  useEffect(() => {
    // PRIORITY 1: Check PIN session first
    const pinSession = localStorage.getItem('pin_session');
    if (pinSession) {
      console.log('[LIMITED_LOCATION_PROVIDER] 📍 PIN session detected');
      try {
        const parsed = JSON.parse(pinSession);

        const locationData: Location = {
          id: parsed.location_id || parsed.location?.id || '',
          name: parsed.location?.name || parsed.store_location || 'Unknown Location',
          code: parsed.location?.code,
          company_id: parsed.company?.id || parsed.profile?.company_id
        };

        console.log('[LIMITED_LOCATION_PROVIDER] ✅ Location from PIN session:', locationData);

        // CRITICAL FIX: Only update ref if different
        if (locationRef.current?.id !== locationData.id) {
          console.log('[LIMITED_LOCATION_PROVIDER] Updating location ref');
          locationRef.current = locationData;
        } else {
          console.log('[LIMITED_LOCATION_PROVIDER] Location unchanged, keeping same object reference');
        }

        setIsLoading(false);
        hasInitialized.current = true;
        return;
      } catch (error) {
        console.error('[LIMITED_LOCATION_PROVIDER] ❌ Error parsing PIN session:', error);
        setIsLoading(false);
      }
    }

    // PRIORITY 2: Check kiosk unlock data
    // KioskView stores under 'kiosk_unlock', SharedLogin stores under 'kiosk_unlocked'
    const kioskUnlockedRaw = localStorage.getItem('kiosk_unlocked') || localStorage.getItem('kiosk_unlock');
    if (kioskUnlockedRaw) {
      try {
        const parsed = JSON.parse(kioskUnlockedRaw);

        if (parsed.location_id || parsed.location) {
          const locationData: Location = {
            id: parsed.location_id || parsed.location?.id || '',
            name: parsed.location?.name || 'Unknown Location',
            code: parsed.location_code || parsed.location?.code,
            company_id: parsed.location?.company_id || parsed.company?.id
          };

          if (locationRef.current?.id !== locationData.id) {
            locationRef.current = locationData;
          }

          setIsLoading(false);
          hasInitialized.current = true;
        }
      } catch (error) {
        console.error('[LIMITED_LOCATION_PROVIDER] Error parsing kiosk unlock data:', error);
        setIsLoading(false);
      }
    }
  }, []); // Empty deps - only runs once on mount
  
  // PHASE 1.5: Listen for PIN session updates (when user logs in after provider mounted)
  useEffect(() => {
    const handlePinSessionUpdate = () => {
      const pinSession = localStorage.getItem('pin_session');
      if (pinSession && !hasInitialized.current) {
        console.log('[LIMITED_LOCATION_PROVIDER] 🔄 PIN session updated detected');
        try {
          const parsed = JSON.parse(pinSession);
          
          const locationData: Location = {
            id: parsed.location_id || parsed.location?.id || '',
            name: parsed.location?.name || parsed.store_location || 'Unknown Location',
            code: parsed.location?.code,
            company_id: parsed.company?.id || parsed.profile?.company_id
          };
          
          console.log('[LIMITED_LOCATION_PROVIDER] ✅ Location from updated PIN session:', locationData);
          
          locationRef.current = locationData;
          setIsLoading(false);
          hasInitialized.current = true;
        } catch (error) {
          console.error('[LIMITED_LOCATION_PROVIDER] ❌ Error parsing updated PIN session:', error);
        }
      }
    };

    // Listen for custom event and storage events
    window.addEventListener('pin_session_updated', handlePinSessionUpdate);
    window.addEventListener('storage', handlePinSessionUpdate);
    
    return () => {
      window.removeEventListener('pin_session_updated', handlePinSessionUpdate);
      window.removeEventListener('storage', handlePinSessionUpdate);
    };
  }, []); // Run once to set up listeners
  
  // PHASE 2: Handle email-authenticated users (depends on auth context)
  useEffect(() => {
    // Skip if already initialized (PIN session handled it)
    if (hasInitialized.current) {
      return;
    }
    
    // Skip if auth not ready yet
    if (!user || !profile || loginMethod !== 'email') {
      return;
    }
    
    const initializeEmailLocation = async () => {
      console.log('[LIMITED_LOCATION_PROVIDER] 📧 Email-authenticated employee detected');
      
      try {
        // Get user's assigned locations from user_locations table
        const { data: userLocations, error: userLocError } = await supabase
          .from('user_locations')
          .select(`
            location_id,
            is_primary,
            locations (
              id,
              name,
              code,
              company_id
            )
          `)
          .eq('user_id', user.id)
          .order('is_primary', { ascending: false });
        
        if (userLocError) {
          console.error('[LIMITED_LOCATION_PROVIDER] ❌ Error fetching user locations:', userLocError);
        } else if (userLocations && userLocations.length > 0) {
          // Use primary location or first available
          const firstLocation = userLocations[0].locations;
          if (firstLocation) {
            console.log('[LIMITED_LOCATION_PROVIDER] ✅ Location from user_locations:', firstLocation);
            locationRef.current = firstLocation as Location;
            setIsLoading(false);
            hasInitialized.current = true;
            return;
          }
        }
        
        // Fallback: Get first active location for company
        if (effectiveCompanyId) {
          console.log('[LIMITED_LOCATION_PROVIDER] 🔍 Fetching first company location');

          const { data: locationData, error } = await supabase
            .from('locations')
            .select('id, name, code, company_id')
            .eq('company_id', effectiveCompanyId)
            .eq('is_active', true)
            .order('name')
            .limit(1)
            .single();
          
          if (error) {
            console.error('[LIMITED_LOCATION_PROVIDER] ❌ Error fetching company location:', error);
          } else if (locationData) {
            console.log('[LIMITED_LOCATION_PROVIDER] ✅ Location from company:', locationData);
            locationRef.current = locationData;
            setIsLoading(false);
            hasInitialized.current = true;
            return;
          }
        }
        
        console.warn('[LIMITED_LOCATION_PROVIDER] ⚠️ No location found for email user');
        setIsLoading(false);
        hasInitialized.current = true;
      } catch (error) {
        console.error('[LIMITED_LOCATION_PROVIDER] ❌ Error initializing location:', error);
        setIsLoading(false);
        hasInitialized.current = true;
      }
    };
    
    initializeEmailLocation();
  }, [user, profile, loginMethod, effectiveCompanyId]); // Re-run when auth state or resolved company changes
  
  // STABILITY FIX: Memoize context value with STABLE location ref
  const contextValue = useMemo(() => {
    const currentLoc = locationRef.current;
    console.log('[LIMITED_LOCATION_CONTEXT] Context value created/updated', {
      locationId: currentLoc?.id,
      locationName: currentLoc?.name,
      isLoading,
      timestamp: new Date().toISOString()
    });
    
    return {
      currentLocation: currentLoc,
      availableLocations: currentLoc ? [currentLoc] : [],
      isLoadingLocation: isLoading,
      canSwitchLocations: false,
      isAllLocations: false,
      setAllLocationsView: () => {},
      setCurrentLocation: () => {
        console.warn('[LIMITED_LOCATION_PROVIDER] setCurrentLocation called but ignored - Limited view cannot switch locations');
      },
      switchLocation: async () => {
        console.warn('[LIMITED_LOCATION_PROVIDER] switchLocation called but ignored - Limited view cannot switch locations');
      },
      refreshLocations: async () => {
        console.warn('[LIMITED_LOCATION_PROVIDER] refreshLocations called but ignored - Limited view uses static location data');
      }
    };
  }, [isLoading]); // CRITICAL: Only depend on isLoading, location ref is stable
  
  return (
    <SharedLocationContext.Provider value={contextValue}>
      {children}
    </SharedLocationContext.Provider>
  );
};

// PHASE 1: Wrap with React.memo to prevent unnecessary re-renders
// Only re-render if auth state actually changes
export const LimitedLocationProvider = memo(
  LimitedLocationProviderComponent,
  (prevProps, nextProps) => {
    // Children prop is stable (doesn't change), so always return true to prevent re-renders
    return true;
  }
);
